/**
 * Scheduling guide — cron and interval triggers for automated task execution.
 *
 * Covers creating schedule triggers via the SDK, common cron patterns,
 * timezone support, and managing triggers.
 */

export const schedulingContent = `
Automate task execution on a recurring schedule using cron expressions or fixed intervals.

## Cron Schedule

Run a task on a recurring schedule using cron expressions:

\`\`\`python
from m8tes import M8tes

client = M8tes()

bot = client.teammates.create(name="Report Bot", tools=["gmail"])

# 1. Create a task
task = client.tasks.create(
    teammate_id=bot.id,
    instructions="Send weekly ticket summary to team@acme.com"
)

# 2. Attach a cron trigger — every Monday at 9am Eastern
trigger = client.tasks.triggers.create(
    task.id,
    type="schedule",
    cron="0 9 * * 1",
    timezone="America/New_York"
)

print(trigger.id, trigger.cron)  # 5, "0 9 * * 1"
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/tasks/1/triggers \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "schedule", "cron": "0 9 * * 1", "timezone": "America/New_York"}'
\`\`\`

### Common Cron Patterns

| Pattern | Schedule |
|---------|----------|
| \`0 9 * * *\` | Daily at 9am |
| \`0 9 * * 1\` | Every Monday at 9am |
| \`0 9 * * 1-5\` | Weekdays at 9am |
| \`0 */6 * * *\` | Every 6 hours |
| \`0 0 1 * *\` | First of each month at midnight |

## Interval Schedule

Run at a fixed interval (in seconds):

\`\`\`python
task = client.tasks.create(
    teammate_id=bot.id,
    instructions="Check for new support tickets"
)

# Every hour
trigger = client.tasks.triggers.create(
    task.id,
    type="schedule",
    interval_seconds=3600
)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/tasks/1/triggers \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "schedule", "interval_seconds": 3600}'
\`\`\`

## Managing Triggers

\`\`\`python
# List all triggers for a task
triggers = client.tasks.triggers.list(task.id)
for t in triggers:
    print(t.id, t.type, t.cron or t.interval_seconds)

# Delete a trigger
client.tasks.triggers.delete(task.id, trigger_id=5)
\`\`\`

\`\`\`bash
# List triggers
curl https://m8tes.ai/api/v2/tasks/1/triggers \\
  -H "Authorization: Bearer m8_your_key_here"

# Delete a trigger
curl -X DELETE https://m8tes.ai/api/v2/tasks/1/triggers/5 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## What's Next

- [Webhook Triggers](/docs/webhook-triggers) — trigger tasks from external systems via HTTP
- [Email Inbox](/docs/email-inbox) — trigger runs from inbound emails
- [Outbound Webhooks](/docs/webhooks-guide) — get notified when runs complete or fail
`;
